import React, { useEffect, useState } from 'react';
import { History, Search, Filter, DollarSign, ArrowLeft, Eye, X, Download, Lock } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import alertService from '../services/alertService';
import { caixaService } from '../services/caixaService';
import { listarMovimentacoesPorCaixa } from '../services/movimentacoesCaixaService';
import { getCaixaById } from '../services/caixasService';
import { MovimentacaoCaixaResponseDTO } from '../types/caixa';
import { authService } from '../services/authService';

const formatCurrency = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const formatDate = (dateString?: string) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('pt-BR');
};

const formatDateTime = (dateString?: string) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('pt-BR');
};

const CaixaMovimentacaoPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const caixaId = id;
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  
  // Dados do caixa
  const [caixaInfo, setCaixaInfo] = useState<any>(null);
  const [saldoAtual, setSaldoAtual] = useState<number>(0);

  // Helper para status do caixa
  const isCaixaFechado = String(caixaInfo?.status || '').toUpperCase() === 'FECHADO';
  
  // Movimentações
  const [movimentacoes, setMovimentacoes] = useState<MovimentacaoCaixaResponseDTO[]>([]);
  const [tiposMovimentacao, setTiposMovimentacao] = useState<string[]>([]);
  
  // Filtros e paginação seguindo padrão de categorias
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [tipoFilter, setTipoFilter] = useState<string>('Todos');
  const [sortOption, setSortOption] = useState<'Padrao' | 'DataAsc' | 'DataDesc' | 'ValorAsc' | 'ValorDesc'>('Padrao');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [pageSize] = useState<number>(10);
  const [showFilters, setShowFilters] = useState<boolean>(false);

  // Modal de detalhes da movimentação
  const [selectedMovimentacao, setSelectedMovimentacao] = useState<MovimentacaoCaixaResponseDTO | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);

  // Modal de criação de movimentação
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [creating, setCreating] = useState<boolean>(false);
  const [newMovement, setNewMovement] = useState<{ tipoMovimentacao: string; valor: number; dataMovimentacao: string; descricao?: string; observacoes?: string }>(() => ({
    tipoMovimentacao: '',
    valor: 0,
    dataMovimentacao: new Date().toISOString().slice(0, 10),
    descricao: '',
    observacoes: '',
  }));

  // Bloqueia o scroll do body quando o modal estiver aberto (detalhes ou criação)
  useEffect(() => {
    if (showCreateModal || showDetailsModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showCreateModal, showDetailsModal]);

  // Carrega informações do caixa
  const loadCaixaInfo = async () => {
    if (!caixaId) return;
    
    try {
      const info = await getCaixaById(caixaId);
      setCaixaInfo(info);
      setSaldoAtual(Number(info?.saldoAtual ?? 0));
    } catch (e: any) {
      console.warn('Erro ao carregar informações do caixa:', e?.message);
    }
  };

  // Carrega saldo atual
  const loadSaldoAtual = async () => {
    if (!caixaId) return;
    
    try {
      const saldoResp = await caixaService.getSaldoAtual(caixaId);
      setSaldoAtual(Number(saldoResp.data ?? 0));
    } catch (e: any) {
      console.warn('Erro ao carregar saldo atual:', e?.message);
      setSaldoAtual(0);
    }
  };

  // Carrega tipos de movimentação
  const loadTiposMovimentacao = async () => {
    try {
      const tiposResp = await caixaService.getMovementTypes();
      setTiposMovimentacao(tiposResp.data ?? []);
    } catch (e: any) {
      console.warn('Erro ao carregar tipos de movimentação:', e?.message);
      // Fallback para tipos estáticos
      setTiposMovimentacao([
        'Abertura de Caixa',
        'Fechamento de Caixa',
        'Entrada',
        'Saída',
        'Transferência',
        'Ajuste'
      ]);
    }
  };

  // Carrega movimentações
  const loadMovimentacoes = async (opts?: { resetPage?: boolean }) => {
    if (!caixaId) return;
    
    setLoading(true);
    setError('');
    
    try {
      if (opts?.resetPage) setCurrentPage(0);
      
      let sortParam: string | undefined;
      switch (sortOption) {
        case 'DataAsc':
          sortParam = 'dataMovimentacao,asc';
          break;
        case 'DataDesc':
          sortParam = 'dataMovimentacao,desc';
          break;
        case 'ValorAsc':
          sortParam = 'valor,asc';
          break;
        case 'ValorDesc':
          sortParam = 'valor,desc';
          break;
        default:
          sortParam = undefined;
      }
      
      const pageResp = await listarMovimentacoesPorCaixa({
        caixaId,
        page: currentPage,
        size: pageSize,
        sort: sortParam,
        tipoMovimentacao: tipoFilter === 'Todos' ? undefined : tipoFilter
      });
      const pageData = pageResp.data;
      
      setMovimentacoes(pageData.content || []);
      setTotalPages(Math.max(1, pageData.totalPages || 1));
      setTotalElements(pageData.totalElements || 0);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Falha ao carregar movimentações');
      setMovimentacoes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (caixaId) {
      loadCaixaInfo();
      // loadSaldoAtual(); // não necessário: saldo já vem de /v1/caixas/:id
      loadTiposMovimentacao();
    }
  }, [caixaId]);

  useEffect(() => {
    loadMovimentacoes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sortOption, tipoFilter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(0);
    loadMovimentacoes({ resetPage: true });
  };

  const handleViewDetails = (movimentacao: MovimentacaoCaixaResponseDTO) => {
    setSelectedMovimentacao(movimentacao);
    setShowDetailsModal(true);
  };

  const openCreateModal = () => {
    if (isCaixaFechado) {
      alertService.info('Este caixa está FECHADO. Não é possível criar movimentações.');
      return;
    }
    setNewMovement((prev) => ({
      ...prev,
      dataMovimentacao: new Date().toISOString().slice(0, 10),
    }));
    setShowCreateModal(true);
  };

  const handleCreateMovement = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isCaixaFechado) {
      alertService.error('Caixa FECHADO. Criação de movimentações não permitida.');
      return;
    }
    if (!newMovement.tipoMovimentacao) {
      alertService.error('Selecione o tipo de movimentação.');
      return;
    }
    if (!newMovement.valor || Number(newMovement.valor) <= 0) {
      alertService.error('Informe um valor maior que zero.');
      return;
    }
    if (!newMovement.dataMovimentacao) {
      alertService.error('Informe a data da movimentação.');
      return;
    }

    try {
      setCreating(true);
      const usuarioId = authService.getLoginData()?.usuarioId || authService.getUser()?.id || undefined;
      const payload: any = {
        ...newMovement,
        valor: Number(newMovement.valor || 0),
        caixaId,
        usuarioId,
      };
      await caixaService.createMovement(payload);
      alertService.success('Movimentação criada com sucesso.');
      setShowCreateModal(false);
      setNewMovement({ tipoMovimentacao: '', valor: 0, dataMovimentacao: new Date().toISOString().slice(0, 10), descricao: '', observacoes: '' });
      await loadMovimentacoes({ resetPage: true });
      await loadCaixaInfo();
    } catch (e: any) {
      alertService.error(e?.response?.data?.message || e?.message || 'Falha ao criar movimentação.');
    } finally {
      setCreating(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setTipoFilter('Todos');
    setSortOption('Padrao');
    setCurrentPage(0);
  };

  // Filtra movimentações localmente por termo de busca
  const filteredMovimentacoes = movimentacoes.filter(mov => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      mov.descricao?.toLowerCase().includes(term) ||
      mov.observacoes?.toLowerCase().includes(term) ||
      mov.usuarioNome?.toLowerCase().includes(term) ||
      mov.tipoMovimentacao?.toLowerCase().includes(term)
    );
  });

  if (!caixaId) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Caixa não encontrado</h3>
          <p className="text-gray-600 mb-4">O ID do caixa não foi fornecido.</p>
          <button
            onClick={() => navigate('/caixas')}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar para Caixas
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header seguindo padrão de categorias */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/caixas')}
            className="text-gray-600 hover:text-gray-800 p-1 rounded"
            title="Voltar"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <History className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              Movimentações do Caixa #{caixaId}
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              Acompanhe as movimentações do caixa
            </p>
          </div>
        </div>
        {/* Botão Nova Movimentação */}
        <div className="flex items-center">
          <button
            onClick={openCreateModal}
            className={`bg-red-600 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2 ${isCaixaFechado ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-700'}`}
            disabled={isCaixaFechado}
            title={isCaixaFechado ? 'Caixa fechado - não é possível criar movimentações' : 'Criar nova movimentação'}
          >
            <span className="inline-block w-4 h-4">+</span>
            Nova Movimentação
          </button>
        </div>
      </div>

      {/* Resumo do Caixa */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col items-center">
            <DollarSign className="h-8 w-8 text-green-600" />
            <div className='text-center'>
              <p className="text-sm font-medium text-gray-500">Saldo Atual</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(saldoAtual)}</p>
            </div>
            
          </div>
        </div>
        {/* Valor de Abertura informado */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col items-center">
            <DollarSign className="h-8 w-8 text-gray-600" />
            <div className='text-center'>
              <p className="text-sm font-medium text-gray-500">Abertura</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(Number(caixaInfo?.valorAberturaInformado ?? 0))}</p>
            </div>
            
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col items-center">
            <DollarSign className="h-8 w-8 text-gray-600" />
            <div className='text-center'>
              <p className="text-sm font-medium text-gray-500">Fechamento</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(Number(caixaInfo?.valorFechamentoInformado ?? 0))}</p>
            </div>
            
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col items-center align-center">
            <History className="h-8 w-8 text-blue-600" />
            <div className='text-center'>
              <p className="text-sm font-medium text-gray-500">Movimentações</p>
              <p className="text-2xl font-bold text-gray-900">{totalElements}</p>
            </div>
            
          </div>
        </div>
        
        {/* Página card removido */}

        {/* Status do Caixa */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col items-center">
            {isCaixaFechado ? (
              <Lock className="h-8 w-8 text-red-600" />
            ) : (
              <History className="h-8 w-8 text-green-600" />
            )}
            <div className='text-center'>
              <p className="text-sm font-medium text-gray-600">Status</p>
              <div className="mt-1">
                <span className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full ${isCaixaFechado ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                  {String(caixaInfo?.status || '-')}
                </span>
              </div>
            </div>
            
          </div>
          {isCaixaFechado && (
            <p className="text-xs text-red-700 mt-2"></p>
          )}
        </div>
      </div>

      {/* Search and Filters seguindo padrão de categorias */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4 sm:items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar movimentações
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Digite descrição, observações, usuário ou tipo..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 sm:gap-4">
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`flex-1 sm:flex-none px-4 py-2 border rounded-lg flex items-center justify-center gap-2 transition-colors ${
                showFilters
                  ? 'bg-red-50 border-red-200 text-red-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              <span className="hidden sm:inline">Filtros</span>
            </button>
            
            <button
              type="submit"
              className="flex-1 sm:flex-none bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
              disabled={loading}
            >
              Buscar
            </button>

            {/* Exportar Excel com resumo + movimentações */}
            <button
              type="button"
              onClick={async () => {
                try {
                  if (!caixaId) return alertService.error('Caixa inválido');
                  alertService.loading('Gerando arquivo...');
                  // Garantir informações do caixa
                  const info = caixaInfo || (await getCaixaById(caixaId));
                  const total = Math.max(totalElements, pageSize);
                  let sortParam: string | undefined;
                  switch (sortOption) {
                    case 'DataAsc': sortParam = 'dataMovimentacao,asc'; break;
                    case 'DataDesc': sortParam = 'dataMovimentacao,desc'; break;
                    case 'ValorAsc': sortParam = 'valor,asc'; break;
                    case 'ValorDesc': sortParam = 'valor,desc'; break;
                    default: sortParam = undefined;
                  }
                  const resp = await listarMovimentacoesPorCaixa({
                    caixaId,
                    page: 0,
                    size: total,
                    sort: sortParam,
                    tipoMovimentacao: tipoFilter === 'Todos' ? undefined : tipoFilter,
                  });
                  const rows = (resp.data?.content ?? []) as MovimentacaoCaixaResponseDTO[];
                  if (rows.length === 0) {
                    alertService.close();
                    return alertService.info('Nada a exportar');
                  }
                  const sep = ';';
                  const esc = (v: any) => {
                    const s = String(v ?? '');
                    const needsQuotes = s.includes(sep) || s.includes('\n') || s.includes('"');
                    const replaced = s.replace(/"/g,'""');
                    return needsQuotes ? `"${replaced}"` : replaced;
                  };
                  // Totais simples baseados no sinal
                  const totalEntradas = rows.filter(r => Number(r.valor ?? 0) >= 0).reduce((sum, r) => sum + Number(r.valor ?? 0), 0);
                  const totalSaidas = rows.filter(r => Number(r.valor ?? 0) < 0).reduce((sum, r) => sum + Number(r.valor ?? 0), 0);

                  const resumoLines = [
                    ['Resumo do Caixa'],
                    ['Caixa ID', info.id],
                    ['Status', info.status ?? ''],
                    ['Data Abertura', formatDate(info.dataAbertura)],
                    ['Valor Abertura', String(info.valorAbertura ?? 0).replace('.', ',')],
                    ['Data Fechamento', formatDate(info.dataFechamento)],
                    ['Saldo Atual', String(info.saldoAtual ?? 0).replace('.', ',')],
                    ['Valor Fechamento Informado', String(info.valorFechamentoInformado ?? 0).replace('.', ',')],
                    ['Movimentações', String(rows.length)],
                    ['Total Entradas', String(totalEntradas).replace('.', ',')],
                    ['Total Saídas', String(totalSaidas).replace('.', ',')],
                  ].map(line => line.map(esc).join(sep));

                  const headers = ['ID','Data','Tipo','Descrição','Valor','Usuário','Observações'];
                  const movimentosLines = [headers.join(sep), ...rows.map(r => [
                    r.id,
                    formatDateTime(r.dataCriacao ?? r.dataMovimentacao),
                    r.tipoMovimentacao,
                    r.descricao ?? '',
                    String(r.valor ?? 0).replace('.', ','),
                    r.usuarioNome ?? '',
                    r.observacoes ?? ''
                  ].map(esc).join(sep))];

                  const bom = '\ufeff';
                  const content = [...resumoLines, '', ...movimentosLines].join('\n');
                  const blob = new Blob([bom + content], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  const now = new Date();
                  const pad = (n: number) => n.toString().padStart(2, '0');
                  const fname = `caixa_${caixaId}_movimentacoes_${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}.csv`;
                  a.download = fname;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  alertService.close();
                } catch (e: any) {
                  alertService.close();
                  alertService.error('Falha ao exportar movimentações', e?.message || 'Tente novamente');
                }
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 inline-flex items-center gap-2"
              title="Exportar para Excel"
            >
              <Download className="h-4 w-4" /> Exportar Excel
            </button>
          </div>
        </form>

        {/* Filtros expandidos */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Movimentação
                </label>
                <select
                  value={tipoFilter}
                  onChange={(e) => setTipoFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="Todos">Todos os tipos</option>
                  {tiposMovimentacao.map((tipo) => (
                    <option key={tipo} value={tipo}>
                      {tipo}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ordenação
                </label>
                <select
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value as 'Padrao' | 'DataAsc' | 'DataDesc' | 'ValorAsc' | 'ValorDesc')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="Padrao">Padrão</option>
                  <option value="DataDesc">Data (Mais recente)</option>
                  <option value="DataAsc">Data (Mais antiga)</option>
                  <option value="ValorDesc">Valor (Maior)</option>
                  <option value="ValorAsc">Valor (Menor)</option>
                </select>
              </div>
            </div>
            
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={clearFilters}
                className="text-red-600 hover:text-red-700 text-sm font-medium"
              >
                Limpar filtros
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="mb-4">
        <p className="text-sm text-gray-600">
          {loading ? (
            'Carregando...'
          ) : (
            `Mostrando ${filteredMovimentacoes.length} de ${totalElements} movimentações`
          )}
        </p>
      </div>

      {/* Movimentações Table seguindo padrão de categorias */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <History className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Erro ao carregar movimentações</h3>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => loadMovimentacoes({ resetPage: true })}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2"
            >
              Tentar novamente
            </button>
          </div>
        ) : filteredMovimentacoes.length === 0 ? (
          <div className="text-center py-12">
            <History className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma movimentação encontrada</h3>
            <p className="text-gray-600 mb-4">
              Não há movimentações para este caixa ou que correspondam aos filtros aplicados.
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Descrição
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usuário
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredMovimentacoes.map((mov) => (
                    <tr key={mov.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatDateTime(mov.dataCriacao ?? mov.dataMovimentacao)?.split(' ')[0]}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDateTime(mov.dataCriacao ?? mov.dataMovimentacao)?.split(' ')[1]}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {mov.tipoMovimentacao}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {mov.descricao || '-'}
                        </div>
                        {mov.observacoes && (
                          <div className="text-sm text-gray-500 max-w-xs truncate">
                            {mov.observacoes}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-medium ${
                          (mov.valor || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {formatCurrency(mov.valor || 0)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {mov.usuarioNome || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleViewDetails(mov)}
                          className="text-blue-600 hover:text-blue-700 p-1 rounded"
                          title="Ver detalhes"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {filteredMovimentacoes.map((mov) => (
                <div key={mov.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <History className="h-4 w-4 text-red-600 mr-2" />
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {mov.tipoMovimentacao}
                        </span>
                      </div>
                      <div className={`text-lg font-medium ${
                        (mov.valor || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(mov.valor || 0)}
                      </div>
                    </div>
                    <button
                      onClick={() => handleViewDetails(mov)}
                      className="text-blue-600 hover:text-blue-700 p-2 rounded"
                      title="Ver detalhes"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-3 text-xs">
                    <div>
                      <span className="text-gray-500 block">Data:</span>
                      <span className="text-gray-900">{formatDateTime(mov.dataCriacao ?? mov.dataMovimentacao)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Descrição:</span>
                      <span className="text-gray-900">{mov.descricao || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Usuário:</span>
                      <span className="text-gray-900">{mov.usuarioNome || '-'}</span>
                    </div>
                    {mov.observacoes && (
                      <div>
                        <span className="text-gray-500 block">Observações:</span>
                        <span className="text-gray-900">{mov.observacoes}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Pagination seguindo padrão de categorias */}
      {totalPages > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-xs sm:text-sm text-gray-700 order-2 sm:order-1">
            Página {currentPage + 1} de {totalPages}
          </div>
          <div className="flex gap-1 sm:gap-2 order-1 sm:order-2">
            <button
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              className="px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            
            {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
              const pageNum = Math.max(0, Math.min(totalPages - 3, currentPage - 1)) + i;
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-2 sm:px-3 py-2 border rounded-lg text-sm font-medium ${
                    currentPage === pageNum
                      ? 'bg-red-600 border-red-600 text-white'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {pageNum + 1}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
              disabled={currentPage === totalPages - 1}
              className="px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      {/* Modal de Detalhes da Movimentação */}
      {showDetailsModal && selectedMovimentacao && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setShowDetailsModal(false)}></div>
          <div role="dialog" aria-modal="true" className="relative bg-white rounded border shadow-lg w-full max-w-xl sm:max-w-2xl p-0 flex flex-col max-h-[90vh] min-h-0 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-lg font-medium text-gray-900">
                Detalhes da Movimentação #{selectedMovimentacao.id}
              </h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-600 hover:text-gray-800 p-1 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-24">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Informações da Movimentação</h4>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm text-gray-500">Tipo:</span>
                      <span className="ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {selectedMovimentacao.tipoMovimentacao}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Data e Hora:</span>
                      <p className="text-sm text-gray-900">{formatDateTime(selectedMovimentacao.dataCriacao ?? selectedMovimentacao.dataMovimentacao)}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Valor:</span>
                      <p className={`text-sm font-medium ${
                        (selectedMovimentacao.valor || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(selectedMovimentacao.valor || 0)}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Usuário:</span>
                      <p className="text-sm text-gray-900">{selectedMovimentacao.usuarioNome || '-'}</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Descrição e Observações</h4>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm text-gray-500">Descrição:</span>
                      <p className="text-sm text-gray-900">{selectedMovimentacao.descricao || '-'}</p>
                    </div>
                    {selectedMovimentacao.observacoes && (
                      <div>
                        <span className="text-sm text-gray-500">Observações:</span>
                        <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                          {selectedMovimentacao.observacoes}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="sticky bottom-0 bg-white border-t px-4 py-3 flex justify-end">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Nova Movimentação */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setShowCreateModal(false)}></div>
          <div role="dialog" aria-modal="true" className="relative bg-white rounded border shadow-lg w-full max-w-xl sm:max-w-2xl p-0 flex flex-col max-h-[90vh] min-h-0 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Registrar Movimentação</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-600 hover:text-gray-800 p-1 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form id="createMovementForm" onSubmit={handleCreateMovement} className="flex-1 min-h-0 overflow-y-auto px-4 pb-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                {/* Cabeçalho do Caixa no topo (padrão do estoque) */}
                {caixaInfo && (
                  <div className="md:col-span-2 flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
                    <DollarSign className="h-5 w-5 text-red-600" />
                    <div className="flex-1">
                      <div className="text-sm text-gray-500">Caixa</div>
                      <div className="text-base font-semibold text-gray-900">#{caixaId} • {String(caixaInfo?.status || '-')}</div>
                      <div className="text-xs text-gray-600">Saldo atual: {formatCurrency(Number(saldoAtual || 0))}</div>
                    </div>
                  </div>
                )}

                <div className="md:col-span-2 flex flex-col">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Movimentação</label>
                  <select
                    value={newMovement.tipoMovimentacao}
                    onChange={(e) => setNewMovement({ ...newMovement, tipoMovimentacao: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    <option value="">Selecione um tipo</option>
                    {tiposMovimentacao.map((tipo) => (
                      <option key={tipo} value={tipo}>{tipo}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2 flex flex-col">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newMovement.valor}
                    onChange={(e) => setNewMovement({ ...newMovement, valor: Number(e.target.value) })}
                    className="w-full px-4 py-3 border-2 border-yellow-400 rounded-lg bg-yellow-50 text-2xl font-bold tracking-wide focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    placeholder="0,00"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data da Movimentação</label>
                  <input
                    type="date"
                    value={newMovement.dataMovimentacao}
                    onChange={(e) => setNewMovement({ ...newMovement, dataMovimentacao: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                  <input
                    type="text"
                    value={newMovement.descricao ?? ''}
                    onChange={(e) => setNewMovement({ ...newMovement, descricao: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Opcional"
                  />
                </div>

                <div className="md:col-span-2 flex flex-col">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    value={newMovement.observacoes ?? ''}
                    onChange={(e) => setNewMovement({ ...newMovement, observacoes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    rows={3}
                    placeholder="Opcional"
                  />
                </div>
              </div>
            </form>
            <div className="sticky bottom-0 bg-white border-t px-4 py-3 flex justify-end gap-3">
              <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                Cancelar
              </button>
              <button type="submit" form="createMovementForm" disabled={creating || isCaixaFechado} className={`bg-red-600 text-white px-6 py-2 rounded-lg disabled:opacity-50 ${isCaixaFechado ? '' : 'hover:bg-red-700'}`}>
                {isCaixaFechado ? 'Caixa fechado' : (creating ? 'Salvando...' : 'Criar Movimentação')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaixaMovimentacaoPage;